import { Box, Typography } from "@mui/material";
import CustomToolTip from "../../../../components/CustomTooltip";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  AWSServiceType,
  AzureServiceType,
  Platforms,
} from "../../../../utils/constants";

import { IntegrationsMapping } from "../../integrations/IntegrationsMapping";
import {
  getMacOSName,
  getWindowsOSName,
} from "../components/entitiesCharts/hostChart/osVersionDistribution/osVersionHelpers";
import { ReactComponent as AMI } from "../icons/logos/AMI.svg";
import {
  getServerOSVersion,
  platformIcons,
  ServerTypeIconsAndLabels,
} from "../utils";

export const getServerColumns = (colors) => ({
  type: {
    field: "type",
    headerName: "Type",
    minWidth: 90,
    flex: 0.5,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    resizable: false,
    valueGetter: (params) => {
      if (params.row?.aws_service) return params.row.aws_service;
      else if (params.row?.cloudflare_data?.server_type)
        return IntegrationsMapping.cloudflare.displayName;
      else if (params.row?.azure_vm_data)
        return params.row.azure_vm_data.resource_type;
      return null;
    },
    valueFormatter: (params) => {
      if (!params.value) return null;
      if (!ServerTypeIconsAndLabels[params.value]) return params.value;
      const [, label] = ServerTypeIconsAndLabels[params.value];
      return label;
    },
    renderCell: (params) => {
      if (!params.value) return "-";

      if (!ServerTypeIconsAndLabels[params.value]) {
        return (
          <Box display="flex" alignItems="center">
            <Typography variant="caption" color={colors.grey_700}>
              {params.value}
            </Typography>
          </Box>
        );
      }

      const [Icon, label] = ServerTypeIconsAndLabels[params.value];
      return (
        <CustomToolTip title={label} placement="right-end">
          <Box display="flex" alignItems="center">
            <SvgToIcon IconLogo={Icon} width={"24px"} />
          </Box>
        </CustomToolTip>
      );
    },
  },
  os: {
    field: "platform",
    headerName: "OS/Image",
    minWidth: 130,
    flex: 1,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    valueGetter: (params) => {
      // TODO - Consider using the runtime of lambdas that is stored in "platform"
      if (params.row.aws_service === AWSServiceType.LAMBDA) return null;
      else return params.row.platform;
    },
    renderCell: (params) => {
      if (!params.value) return "-";
      const labelComponents = [
        <Typography key="title" variant="caption">
          {params.value}
        </Typography>,
      ];
      if (params.row.aws_service === AWSServiceType.ECS)
        labelComponents.unshift(<SvgToIcon IconLogo={AMI} width={"26px"} />);
      if (
        params.row.aws_service === AWSServiceType.EC2 ||
        params.row.aws_service === AWSServiceType.EKS ||
        (params.row?.azure_vm_data &&
          params.row.azure_vm_data.resource_type === AzureServiceType.VM)
      ) {
        for (const [platform, icon] of Object.entries(platformIcons)) {
          if (params.value.toLowerCase().includes(platform.toLowerCase())) {
            labelComponents.unshift(
              <SvgToIcon key={platform} IconLogo={icon} width={"24px"} />
            );
            break;
          }
        }
      }
      // TODO - Consider adding icons for lambda runtimes (python, nodejs, java...)
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {labelComponents}
        </Box>
      );
    },
  },
  os_version: {
    field: "os_version",
    headerName: "OS Version",
    minWidth: 170,
    flex: 1,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    valueGetter: (params) => {
      if (params.row.aws_service === AWSServiceType.LAMBDA) return null;
      const osVersion = getServerOSVersion(params.row);
      return osVersion && osVersion !== "None" ? osVersion : null;
    },
    renderCell: (params) => {
      if (!params.value) return "-";
      const platform = params.row.platform;
      let displayValue;
      if (platform === Platforms.WINDOWS) {
        const formatted = getWindowsOSName(params.value);
        displayValue = formatted === "Windows" ? params.value : formatted;
      } else if (platform === Platforms.MACOS) {
        const formatted = getMacOSName(params.value);
        displayValue =
          formatted === "" || formatted === "MacOS" ? params.value : formatted;
      } else {
        displayValue = params.value;
      }
      return (
        <Typography variant="caption" color={colors.grey_700}>
          {displayValue}
        </Typography>
      );
    },
  },
  image_description: {
    field: "image_description",
    headerName: "Image",
    minWidth: 130,
    flex: 1,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    valueGetter: (params) => {
      if (params.row.aws_service === AWSServiceType.LAMBDA) return null;
      else return params.row.aws_data?.image_description;
    },
    renderCell: (params) => {
      if (!params.value) return "-";
      const labelComponents = [
        <Typography key="title" variant="caption">
          {params.value}
        </Typography>,
      ];
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {labelComponents}
        </Box>
      );
    },
  },
  internet_exposure: {
    field: "is_internet_facing",
    headerName: "Internet Exposure",
    minWidth: 170,
    flex: 1,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    resizable: false,
    valueFormatter: (params) => (params.value ? "Exposed" : "Not Exposed"),
    renderCell: (params) => (
      <Typography variant="caption" color={colors.grey_700}>
        {params.value ? "Exposed" : "Not Exposed"}
      </Typography>
    ),
  },
  vulnerabilities: {
    field: "vulnerability_count",
    headerName: "Vulnerabilities",
    minWidth: 140,
    flex: 0.5,
    align: "left",
    headerAlign: "left",
    disableReorder: true,
    valueGetter: (params) => {
      return params.row.vulnerability_count || 0;
    },
    valueFormatter: (params) => params.value.toString(),
    renderCell: (params) => (
      <Typography variant="caption">{params.value}</Typography>
    ),
  },
});
